import { observer } from 'mobx-react';
import { Input, InputType, Button, InputCalendar } from '../../../../core/components/Index';
import useAsyncEffect from 'use-async-effect';
import ApplyViewModel from './ApplyViewModel';
import { Steps } from './ApplyModels';
import { useParams } from 'react-router-dom';
import { Appl } from '../../../../core/Appl';
import { formatDate } from '../../../../core/services/Helper';
import scerea from '../../../../assets/images/scerea.png'

const PersonalInformation = observer(() => {
  const vm = Appl.Services.get<ApplyViewModel>('ApplyViewModel');
  let { memberId } = useParams();
  useAsyncEffect(async () => {
    await vm.loadStepAsync(Steps.PersonalInformation, memberId!);
  }, []);

  return (
    <>

      {vm.personalInfoEditable &&
        <div className="space-y-6 max-w-4xl">
          <div>
            <h1 className="text-32px tracking--1percent text-neutral-800 font-semibold mb-4">Your Information</h1>
            <p className="text-black text-15px tracking--1percent leading-6">
              Let's get your personal details set up! Just fill in the fields below to make sure everything is accurate before moving forward with your retirement application.
            </p>
            <p className="text-gray-700 font-medium text-22px mt-6" >Personal Information</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-2xl">
            <div>
              <Input
                id="firstName"
                value={vm.model.personalInformation.firstName}
                inputType={InputType.Text}
                label="First Name"
                onChange={(e) => {
                  vm.onInputChange(e, 'firstName', vm.model.personalInformation);
                }}
                required={true}
              />
            </div>
            <div>
              <Input
                id="middleName"
                value={vm.model.personalInformation.middleName}
                inputType={InputType.Text}
                label="Middle"
                onChange={(e) => {
                  vm.onInputChange(e, 'middleName', vm.model.personalInformation);
                }}
              />
            </div>
            <div>
              <Input
                id="lastName"
                value={vm.model.personalInformation.lastName}
                inputType={InputType.Text}
                label="Last Name"
                onChange={(e) => {
                  vm.onInputChange(e, 'lastName', vm.model.personalInformation);
                }}
                required={true}
              />
            </div>
            <div>
              <InputCalendar
                id="birthDate"
                value={vm.model?.personalInformation?.birthDate!}
                label="Date of Birth"
                showHint={true}
                onChange={(e) => {
                  vm.onInputChange(e, 'birthDate', vm.model?.personalInformation);
                }}
                required={true}
                maxDate={new Date()}
              />
            </div>
            <div>
              <label className="font-bold text-15px text-black mb-1 block">
                Social Security Number <span style={{ color: 'red' }}> * </span><span className="font-normal block text-13px">Last 4 digits</span>
              </label>
              <div className='flex flex-wrap'>
                <label className='text-15px text-[#333] mt-3 block font-mono '>XXX-XX-</label>
                <div className='pl-1'>
                  <Input
                    id="ssn"
                    value={vm.model?.personalInformation.ssn}
                    inputType={InputType.Text}
                    onChange={(e) => vm.onInputChange(e, 'ssn', vm.model.personalInformation)}
                    style={{ width: '120px' }}
                    maxLength={4}
                    required={true}
                  />
                </div>
              </div>
            </div>
            <div />
          </div>
          <div className="flex mt-6 flex-wrap gap-2 sm:flex-nowrap">
            <Button title="Save & Continue" rounded
              onClick={async () => await vm.onPersonalInformationStepChangeAsync()}
            />
            {vm.navigatedFromReviewScreen && <Button title="Cancel & Return to Review" severity='secondary' rounded
              onClick={async () => await vm.navigateToReviewStepAsync()}
            />}
          </div>
        </div>
      }
      {!vm.personalInfoEditable && <div className="space-y-6 max-w-4xl">
        <div>
          <h1 className="text-32px tracking--1percent text-neutral-800 font-semibold mb-4">Your Information</h1>
          <p className="text-black text-15px tracking--1percent leading-6 mb-8">Almost there! Take a moment to review the personal details you entered. If you spot anything that needs to be changed, simply click 'Make a Change' and update it before continuing.</p>
          <p className="text-gray-900 text-lg font-medium">Please review your personal information.</p>
          <p className='mt-8'><b>Name:</b> {vm.model.personalInformation.firstName} {vm.model.personalInformation.middleName} {vm.model.personalInformation.lastName}</p>
          <p><b>Date of Birth:</b> {formatDate(vm.model?.personalInformation?.birthDate)}</p>
          <p className='mb-8'><b>SSN:</b> XXX - XX - {vm.model.personalInformation.ssn}</p>
        </div>
        <div className="flex space-x-4 mt-6">
          {!vm.navigatedFromReviewScreen &&
            <Button title="Save & Continue" rounded
              onClick={async () => await vm.onPersonalInformationStepCompleteAsync()}
            />
          }
          {vm.navigatedFromReviewScreen &&
            <Button title="Return to Review" rounded
              onClick={() => vm.navigateToReviewStepAsync()} />
          }
          <Button title="Make a Change" severity="warning" outlined rounded
            onClick={() => vm.personalInfoEditable = true}
          />
        </div>
      </div>
      }
    </>
  );
});

export default PersonalInformation;